import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';

export type EnvironmentRestrictionMode = 'disabled' | 'restricted' | 'unrestricted';
export type EnvironmentWorkflowStatus = 'allowed' | 'caution' | 'blocked' | 'confirm';
export type EnvironmentNoticeSeverity = 'info' | 'warning' | 'danger';
export type EnvironmentInfoStatus = EnvironmentRestrictionMode | EnvironmentWorkflowStatus;

export interface EnvironmentWorkflow {
  key: string;
  label: string;
  status: EnvironmentWorkflowStatus;
  description: string;
  values: string[];
}

export interface EnvironmentNotice {
  title: string;
  severity: EnvironmentNoticeSeverity;
  description: string;
  values: string[];
}

export interface EnvironmentRestriction {
  key: string;
  label: string;
  status: EnvironmentInfoStatus;
  description: string;
  values: string[];
}

export interface EnvironmentInfo {
  environment: {
    label: string;
    production: boolean;
  };
  restrictions: {
    emailDelivery: {
      enabled: boolean;
      mode: EnvironmentRestrictionMode;
      allowedRecipients: string[];
    };
    registrationPlatformPush: {
      mode: Exclude<EnvironmentRestrictionMode, 'disabled'>;
      allowedProjectIds: string[];
    };
    campaignSenderDomains: {
      mode: Exclude<EnvironmentRestrictionMode, 'disabled'>;
      restrictedDomains: string[];
    };
    domainVerification: {
      mode: Exclude<EnvironmentRestrictionMode, 'disabled'>;
      allowedDomains: string[];
    };
    additional: EnvironmentRestriction[];
  };
  workflows: EnvironmentWorkflow[];
  notices: EnvironmentNotice[];
  updatedAt: string | null;
}

interface EnvironmentInfoResponse {
  success: boolean;
  message: string | null;
  data: EnvironmentInfo;
}

@Injectable({ providedIn: 'root' })
export class EnvironmentInfoService {
  private readonly url = `${environment.apiUrl}/api/v1/environment-info`;

  constructor(private http: HttpClient) {}

  get(): Observable<EnvironmentInfoResponse> {
    return this.http.get<EnvironmentInfoResponse>(this.url);
  }
}
